import { useCallback, useMemo } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { SuccessFetchListResponse } from "@/services/api";
import { ReadLaborCostsInStores } from "@/schemas/laborCostsInStores";
import { UseDataTableOptions } from "@/types/dataTableSettings";
import { InvoiceSpecificationLaborCostsConfig } from "./useInvoiceSpecificationLaborCostsList";

export function useInvoiceSpecificationLaborCostsQuery(
  config: InvoiceSpecificationLaborCostsConfig,
  invoiceSpecificationId: string,
  options: UseDataTableOptions<ReadLaborCostsInStores>,
) {
  const queryKey = useDynamicQueryParams(config.name, options);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      config.service.getList(invoiceSpecificationId, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [config.service, invoiceSpecificationId, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadLaborCostsInStores>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  return { query, queryValue };
}
